import { forwardRef, useCallback, useEffect, useState } from "react";
import { FormControl } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ensureArray } from "../utils/arrayUtilities";
import PropTypes from 'prop-types';

const NO_ERRORS = [];

/**
 * Helper to render error and success messages
 * @component
 */
const ValidationResult = ({ errors, success }) => {
    return <>
        {success && <FormControl.Feedback className="px-2 py-1" type="valid" style={{ backgroundColor: '#dff0d8', color: 'black', borderLeft: '1px solid #28a745', borderRight: '1px solid #28a745', borderBottom: '1px solid #28a745', borderEndStartRadius: '4px', borderEndEndRadius: '4px', marginTop: '-4px', display: 'block' }}>
                        <div className="d-flex flex-column">{ensureArray(success).map(s => <div key={s}>{s}</div>)}</div>
                    </FormControl.Feedback>}
        {errors && <FormControl.Feedback className="px-2 py-1" type="invalid" style={{ backgroundColor: '#f8d7da', color: 'black', borderLeft: '1px solid red', borderRight: '1px solid red', borderBottom: '1px solid red', borderEndStartRadius: '4px', borderEndEndRadius: '4px', marginTop: '-4px', display: 'block' }}>
                        <div className="d-flex flex-column">{ensureArray(errors).map(e => <div key={e}>{e}</div>)}</div>
                    </FormControl.Feedback>}
    </>
}
ValidationResult.propTypes = {
    errors: PropTypes.oneOfType([PropTypes.string,PropTypes.arrayOf(PropTypes.string)]),
    success: PropTypes.oneOfType([PropTypes.string,PropTypes.arrayOf(PropTypes.string)])
}

/**
 * A validator component which knows how to handle indirect HTML5 form validation and generate error messages.
 * @component
 */
export const Validator = forwardRef(({ value, children, success, onValidation, nonblockingError }, ctrlRef) => {
    const { t } = useTranslation('common');
    const [errors, setErrors] = useState(NO_ERRORS);

    useEffect(() => {
        setErrors(NO_ERRORS);
    }, [value]);

    const invalidListener = useCallback(() => {
        let errors = [];
        if (nonblockingError) {
            errors.push(nonblockingError);
        }
        const validity = ctrlRef.current?.validity
        if (validity) {
            if (validity.badInput) {
                errors.push(t('validationBad'));
            }
            if (validity.valueMissing) {
                errors.push(t('validationRequired'));
            }
            if (validity.rangeOverflow) {
                const max = ctrlRef.current.getAttribute('max');
                errors.push(t('validationMax', { max }));
            }
            if (validity.rangeUnderflow) {
                const min = ctrlRef.current.getAttribute('min');
                errors.push(t('validationMin', { min }))
            }
            if (validity.stepMismatch) {
                const step = ctrlRef.current.getAttribute('step');
                errors.push(t('validationStep', { step: step ?? 1 }))
            }
            if (validity.tooLong) {
                const max = ctrlRef.current.getAttribute('maxlength');
                errors.push(t('validationTooLong', { max }))
            }
            if (validity.tooShort) {
                const min = ctrlRef.current.getAttribute('minlength');
                errors.push(t('validationTooShort', { min }))
            }
            if (validity.patternMismatch) {
                errors.push(t('validationPattern'))
            }
            if (validity.typeMismatch) {
                const type = ctrlRef.current.getAttribute('type');
                switch (type) {
                    case 'email':
                        errors.push(t('validationEmailInvalid'));
                        break;
                    case 'url':
                        errors.push(t('validationUrlInvalid'));
                        break;
                    default:
                        throw new Error('Unexpected typeMismatch from control with type ' + type);
                }
            }
            if (validity.customError) {
                errors.push(ctrlRef.current.validationMessage);
            }
        }
        setTimeout(() => setErrors(errors?.length > 0 ? errors : NO_ERRORS), 0);
        onValidation?.(!!nonblockingError || errors.length > 0);
    }, [ctrlRef, nonblockingError, onValidation, t])

    const changeListener = useCallback(() => {
        setErrors(NO_ERRORS);
    }, [])

    useEffect(() => {
        let ctrl = ctrlRef.current;
        ctrl?.addEventListener('invalid', invalidListener)
        ctrl?.addEventListener('change', changeListener);
        ctrl?.addEventListener('blur', changeListener);
        ctrl?.addEventListener('input', changeListener);
        return () => {
            ctrl?.removeEventListener('invalid', invalidListener);
            ctrl?.removeEventListener('change', changeListener);
            ctrl?.removeEventListener('blur', changeListener);
            ctrl?.removeEventListener('input', changeListener);
        }
    }, [ctrlRef, invalidListener])

    return <>
            {children}
            <ValidationResult success={success} errors={errors.length > 0 ? errors : nonblockingError}>{errors}</ValidationResult>
    </>
});

Validator.propTypes = {
    children: PropTypes.node,
    success: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    onValidation: PropTypes.func.isRequired,
    nonblockingError: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
}
